import React, { useEffect, useMemo, useState } from 'react'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import { Popover } from 'functional-foundations'
import { EnrollmentType } from '../interface/EnrollmentType'
import { Alert, Badge, Icon } from 'foundations-library/components'
import { failedStatuses, pendingStatuses, rejectedStatuses } from '../utils/statuses'

const brandStatusMapper = (status) => {
    let rv = 'unknown'
    // remote config IDs sms_enrollment_brand_status_*
    switch (status?.toLowerCase()) {
            case 'approved':
                rv = 'verified'
                break
            case 'pending-review':
                rv = 'submitted'
                break
            case 'register-failed':
                rv = 'unverified'
                break
            case 'rejected':
                rv = 'unverified'
                break
            case 'payment-failed':
                rv = 'payment_failed'
                break
            case 'processing':
                rv = 'processing'
                break
            case 'deleted':
                rv = 'deleted'
                break
            case 'in-review': // legacy statuses below
                rv = 'submitted'
                break
            case 'submitted':
                rv = 'submitted'
                break
            case 'unverified':
                rv = 'unverified'
                break
            case 'verified':
                rv = 'verified'
                break
            default:
                break
    }

    return rv
}

const campaignStatusMapper = (status) => {
    let rv = 'unknown'
    // Remote Config IDs sms_enrollment_campaign_status_*
    switch (status?.toLowerCase()) {
            case 'approved':
                rv = 'accepted'
                break
            case 'rejected':
                rv = 'rejected'
                break
            case 'register-failed':
                rv = 'creation_failure'
                break
            case 'payment-failed':
                rv = 'payment_failed'
                break
            case 'processing':
                rv = 'processing'
                break
            case 'pending-review':
                rv = 'pending'
                break
            case 'deleted':
                rv = 'deleted'
                break
            case 'accepted': // legacy statuses
                rv = 'accepted'
                break
            case 'pending':
                rv = 'pending'
                break
            default:
                break
    }

    return rv
}

/**
 *
 */
export const greenStatuses = ['approved', 'accepted', 'verified']
/**
 *
 */
const useStyles = makeStyles(() => {
    return {
        badge: {
            borderRadius: 20,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1
        },
        chipTrigger: {
            display: 'inline-block'
        },
        infoIcon: {
            flexShrink: 0
        }
    }
})

interface StatusChipProps {
    status: string,
    enrollmentType: EnrollmentType,
    deleted: boolean
    compact?: boolean
    itemsAttached?: number | null
    showPopover?: boolean
    latestEvent?: {
        description: string
        event_name: string
        created_at: number
    }
    'data-testid'?: string
}

const PAYMENT_FAILED_MESSAGE =
    'Your registration was not completed due to a payment issue. Please update your payment and try again.'

const BRAND_REJECTED_MESSAGE =
    'We couldn’t approve your Brand registration. Please review the rejection details and edit your submission to continue.'

const CAMPAIGN_REJECTED_MESSAGE =
    'We couldn’t approve your Campaign registration. Please review the rejection details and edit your submission to continue.'

const BRAND_PENDING_MESSAGE =
    'You’ll be able to start your Campaign Registration as soon as your Brand is approved.'

const CAMPAIGN_PENDING_MESSAGE =
    'Your Campaign is pending approval. Messaging will activate once approved.'

const CAMPAIGN_0_NUMBERS_MESSAGE = 'Number assignment is required to activate this Campaign.'

const BRAND_ACCEPTED_0_CAMPAIGNS = 'Great news — your Brand is approved! Please start your Campaign Registration to continue setup.'
/**
 *
 */
const StatusChip = ({
    status,
    enrollmentType,
    deleted,
    itemsAttached = null,
    showPopover = true,
    'data-testid': dataTestId
}: StatusChipProps) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    status = deleted ? 'deleted' : (status || 'pending-review').toLowerCase()

    const color = useMemo(() => {
        if (greenStatuses.includes(status)) return ['neutral-700', 'primary-100']
        if (failedStatuses.includes(status)) return ['neutral-700', 'destructive-100']
        if (pendingStatuses.includes(status)) return ['neutral-700', 'primary2-100']
        return ['neutral-700', 'neutral-100']
    }, [status])

    const label = useMemo(() => {
        if (enrollmentType === EnrollmentType.Brand) {
            if (pendingStatuses.includes(status)) return 'Pending'
            return getValue(`sms_enrollment_brand_status_${brandStatusMapper(status)}`)
        }
        if (pendingStatuses.includes(status)) return 'Pending review'
        return getValue(`sms_enrollment_campaign_status_${campaignStatusMapper(status)}`)
    }, [enrollmentType, status])

    const generateMessage = () => {
        if (enrollmentType === EnrollmentType.Brand) {
            return BRAND_PENDING_MESSAGE
        }
        if (enrollmentType === EnrollmentType.Campaign) {
            if (itemsAttached === 0) {
                return CAMPAIGN_0_NUMBERS_MESSAGE
            }
            return CAMPAIGN_PENDING_MESSAGE
        }
    }

    const chipInfo = useMemo(() => {
        if (!showPopover) return null
        if (status === 'payment-failed') {
            return {
                message: PAYMENT_FAILED_MESSAGE,
                level: 'error' as const,
                withGradient: false,
                iconColor: 'destructive-500'
            }
        }

        if (rejectedStatuses.includes(status)) {
            return {
                message: enrollmentType === EnrollmentType.Brand ? BRAND_REJECTED_MESSAGE : CAMPAIGN_REJECTED_MESSAGE,
                level: 'error' as const,
                withGradient: false,
                iconColor: 'destructive-500'
            }
        }

        if (pendingStatuses.includes(status)) {
            return {
                message: generateMessage(),
                level: 'warning' as const,
                withGradient: true,
                iconColor: 'neutral-700'
            }
        }

        if (status === 'approved' && itemsAttached === 0) {
            return {
                message: enrollmentType === EnrollmentType.Brand ? BRAND_ACCEPTED_0_CAMPAIGNS : CAMPAIGN_0_NUMBERS_MESSAGE,
                level: 'success' as const,
                withGradient: true,
                iconColor: 'neutral-700'
            }
        }

        return null
    }, [status, enrollmentType, itemsAttached])

    const openAlert = (event: React.MouseEvent<HTMLElement>) => {
        if (!chipInfo?.message) return
        setAnchorEl(event.currentTarget)
    }

    const closeAlert = () => {
        setAnchorEl(null)
    }

    const isAlertOpen = Boolean(anchorEl && chipInfo?.message)

    useEffect(() => {
        if (!isAlertOpen) return

        const handleScroll = () => {
            closeAlert()
        }

        window.addEventListener('scroll', handleScroll, true)

        return () => {
            window.removeEventListener('scroll', handleScroll, true)
        }
    }, [isAlertOpen])

    return <>
        <span className={classes.chipTrigger} onMouseEnter={openAlert} onMouseLeave={closeAlert} data-testid={dataTestId}>
            <Badge color={color} className={classes.badge} data-testid={`${dataTestId}-badge`}>
                {label}
                {chipInfo?.message && (
                    <Icon
                        name='infotip-sm'
                        size={21}
                        color={chipInfo.iconColor}
                        className={classes.infoIcon}
                        data-testid={`${dataTestId}-icon`}
                    />
                )}
            </Badge>
        </span>
        <Popover
            open={isAlertOpen}
            anchorEl={anchorEl}
            onClose={closeAlert}
            placement='bottom-left'
            data-testid={`${dataTestId}-popover`}
        >
            {chipInfo?.message && (
                <Alert
                    level={chipInfo.level}
                    withGradient={chipInfo.withGradient}
                    data-testid={`${dataTestId}-popover-alert`}
                >
                    {chipInfo.message}
                </Alert>
            )}
        </Popover>
    </>
}

export default StatusChip
